import { Icon } from '@susu/ui/components'
import { useI18N } from '@susu/i18n/client'

import { useBookingStore } from '../store'

export interface ScreenHeaderProps {
  /** When omitted/empty, the title row is hidden (e.g. confirmation). */
  title?: string
  /** data-testid applied to the <h1> */
  titleTestId?: string
  /**
   * When omitted, the close (✕) button is hidden — used on cancel and
   * reschedule confirmation where Done / Reschedule is the only exit.
   */
  onClose?: () => void
  /**
   * When true (e.g. StoreLocator), the header uses pb-20 because a dropdown
   * sits immediately below and provides visual breathing room.
   * When false (default), pb-40 is used for screens with a plain list below.
   */
  hasDropdown?: boolean
  /**
   * Replaces the back arrow's default `goBack`, for a screen that has to clean
   * up before leaving.
   */
  onBack?: () => void
}

/**
 * Shared booking-tool header.
 * Layout matches Figma node 4006:4317 — two stacked rows:
 *   Row 1: back arrow (left) + close ✕ (right) — each optional
 *   Row 2: screen title, centered — optional
 */
export const ScreenHeader = ({
  title,
  titleTestId,
  onClose,
  hasDropdown = false,
  onBack,
}: ScreenHeaderProps) => {
  const { t } = useI18N()
  const { goBack, screenHistory } = useBookingStore()
  const canGoBack = screenHistory.length > 0
  const showClose = Boolean(onClose)
  const showTitle = Boolean(title)
  const showNavRow = canGoBack || showClose

  const handleClose = () => {
    // `bookingToolPanelClosed` fires from `BookingToolRoot`'s unmount handler,
    // not here — hosts also close the panel by overlay click, Esc, or a route
    // change, none of which reach this button, and firing here as well would
    // double the event for the one path that does.
    onClose?.()
  }

  // Cancel / confirmation: no icons, no title — keep a light top inset only.
  if (!showNavRow && !showTitle) {
    return <header className="pt-12 shrink-0" aria-hidden="true" />
  }

  return (
    <header
      className={`flex flex-col ${showTitle ? 'gap-10' : ''} pt-12 ${showTitle ? (hasDropdown ? 'pb-20' : 'pb-40') : 'pb-12'} px-12 w-full shrink-0`}
    >
      {showNavRow && (
        <div className="flex items-start justify-between w-full">
          {canGoBack ? (
            <button
              type="button"
              onClick={onBack ?? goBack}
              className="p-4 -ml-4 text-icon hover:text-icon-hover transition-[color] duration-150 rounded-full"
              aria-label={t('common.back')}
            >
              <Icon name="arrow-back" size={16} />
            </button>
          ) : (
            <span className="p-4 w-[24px]" aria-hidden="true" />
          )}

          {showClose ? (
            <button
              type="button"
              onClick={handleClose}
              className="p-4 -mr-4 text-icon hover:text-icon-hover transition-[color] duration-150 rounded-full"
              aria-label={t('common.close')}
            >
              <Icon name="close" size={16} />
            </button>
          ) : (
            <span className="p-4 w-[24px]" aria-hidden="true" />
          )}
        </div>
      )}

      {showTitle && (
        <h1
          className="text-[20px] leading-[30px] font-medium text-text text-center tracking-[-0.3px] w-full"
          data-testid={titleTestId}
        >
          {title}
        </h1>
      )}
    </header>
  )
}

export default ScreenHeader
